import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import config from "config";
import { toast } from "react-toastify";
import errorMessages from "./errorMessages";

const { api } = config;

axios.defaults.baseURL = api.API_URL;
axios.defaults.headers.post["Content-Type"] = "application/json";
axios.defaults.headers.put["Content-Type"] = "application/json";

axios.interceptors.request.use(
  function (config) {
    const authUser = sessionStorage.getItem("authUser");

    if (authUser) {
      try {
        const parsed = JSON.parse(authUser);
        const token =
          typeof parsed === "string"
            ? parsed.replaceAll('"', "")
            : parsed?.token;

        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch {
        // authUser adalah plain string langsung (dari superadmin switch)
        config.headers.Authorization = `Bearer ${authUser}`;
      }
    }

    return config;
  },
  function (error) {
    return Promise.reject(error);
  },
);

let lastErrorMessage = "";
let lastErrorTime = 0;

axios.interceptors.response.use(
  function (response) {
    return response.data ? response.data : response;
  },
  function (error) {
    const status = error.response?.status;
    const data = error.response?.data;
    let message: string;

    switch (status) {
      case 500:
        message = "Terjadi kesalahan pada server";
        break;
      case 401:
        window.location.href = "/logout";
        message = "Sesi telah berakhir, silahkan login kembali!";
        break;
      case 404:
        message = "Data yang Anda cari tidak ditemukan";
        break;
      default:
        if (typeof data === "object" && data?.detail) {
          message =
            errorMessages[data.detail] || data.detail || "Terjadi kesalahan";
        } else {
          message = error.message || "Terjadi kesalahan yang tidak diketahui";
        }
    }

    const now = Date.now();
    if (message !== lastErrorMessage || now - lastErrorTime > 3000) {
      toast.error(message);
      lastErrorMessage = message;
      lastErrorTime = now;
    }

    return Promise.reject(message);
  },
);

const setAuthorization = (token: string) => {
  axios.defaults.headers.common["Authorization"] = "Bearer " + token;
};

class APIClient {
  get = (
    url: string,
    params?: any,
    usePath: boolean = false,
  ): Promise<AxiosResponse> => {
    let response: Promise<AxiosResponse>;

    if (params) {
      if (usePath) {
        response = axios.get(`${url}${params}`);
      } else {
        const queryString = Object.keys(params)
          .map((key) => `${key}=${params[key]}`)
          .join("&");

        response = axios.get(queryString ? `${url}?${queryString}` : url);
      }
    } else {
      response = axios.get(url);
    }

    return response;
  };

  create = (url: string, data: any) => {
    return axios.post(url, data);
  };

  update = (url: string, data: any) => {
    return axios.patch(url, data);
  };

  put = (url: string, data?: any) => {
    return axios.put(`${url}`, data);
  };

  createFormData = (url: string, data: FormData): Promise<AxiosResponse> => {
    return axios.post(url, data, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  };

  putFormData = (url: string, data: FormData): Promise<AxiosResponse> => {
    return axios.put(url, data, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  };

  delete = (
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse> => {
    return axios.delete(url, { ...config });
  };

  getBlob = (url: string, params?: any): Promise<AxiosResponse> => {
    const config: AxiosRequestConfig = {
      responseType: "blob",
    };

    if (params) {
      const queryString = Object.keys(params)
        .map((key) => `${key}=${params[key]}`)
        .join("&");

      return axios.get(queryString ? `${url}?${queryString}` : url, config);
    }

    return axios.get(url, config);
  };

  createFormUrlEncoded = (url: string, data: Record<string, any>) => {
    const formData = new URLSearchParams();
    Object.keys(data).forEach((key) => {
      formData.append(key, data[key]);
    });

    return axios.post(url, formData, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });
  };

  createFormDataWithQuery = (
    url: string,
    data: FormData,
    params?: Record<string, any>,
  ): Promise<AxiosResponse> => {
    let fullUrl = url;

    if (params) {
      const queryString = Object.keys(params)
        .map((key) => `${key}=${params[key]}`)
        .join("&");

      if (queryString) fullUrl = `${url}?${queryString}`;
    }

    return axios.post(fullUrl, data, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  };

  putFormDataWithQuery = (
    url: string,
    data: FormData,
    params?: Record<string, any>,
  ): Promise<AxiosResponse> => {
    let fullUrl = url;

    if (params) {
      const queryString = Object.keys(params)
        .map((key) => `${key}=${params[key]}`)
        .join("&");

      if (queryString) fullUrl = `${url}?${queryString}`;
    }

    return axios.put(fullUrl, data, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  };
}

const getLoggedinUser = () => {
  const user = sessionStorage.getItem("authUser");
  if (!user) {
    return null;
  } else {
    return JSON.parse(user);
  }
};

export { APIClient, setAuthorization, getLoggedinUser };
